import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRouter } from "next/router";
import Link from "next/link";

import IconCode from "@/src/lib/iconCode";
import Iconify from "../../iconify/Iconify";
import {
    removeFromCart,
    clearAllCart,
} from "@/src/lib/store/add-to-cart/slice";
import { useToast } from "@/src/lib/hooks/useToast";
import NoTestFoundInCart from "./noTestFoundInCart";
import LoginPopUp from "../../common/loginPopUp";
import CustomButton from "@/src/lib/ui/Button";
import CustomIconButton from "@/src/lib/ui/iconButton";

const CartDrawer = ({ openCart, handleCloseCart }) => {
    const { push } = useRouter();
    const { showCustomToast } = useToast();
    const reduxDispatch = useDispatch();
    const isItemInCart = useSelector((state) => state?.addToCart?.addProducts);

    const [open, setOpen] = useState(false);

    const [isMounted, setIsMounted] = React.useState(false);
    React.useEffect(() => setIsMounted(true), []);
    if (!isMounted) return null; // prevents mismatch

    // ✅ Body scroll lock without useEffect
    if (typeof document !== "undefined") {
        document.body.style.overflow = openCart ? "hidden" : "auto";
    }

    const handleClickOpen = () => {
        setOpen(true);
        handleCloseCart();
    };

    const onClose = () => {
        setOpen(false);
    };

    const totalAmount = isItemInCart?.centreTest?.reduce((acc, item) => {
        return acc + item?.rate;
    }, 0);

    const removeProductFromCart = (itemId) => {
        reduxDispatch(removeFromCart(itemId));
        showCustomToast("Item removed from cart", "error");
    };

    const backToProductPage = (item) => {
        push(`/test/${item?.slug}`);
        handleCloseCart();
    };

    const findTest = () => {
        push("/find-a-test");
        handleCloseCart();
    };

    const clearAllCartHandler = () => {
        reduxDispatch(clearAllCart());
        showCustomToast("All items removed from cart", "error");
    };

    const DrawerList = isItemInCart?.centreTest?.map((item, index) => (
        <div
            key={`${item?.itemId}-${index}`}
            className="bg-white hover:shadow-lg p-3 shadow-xs rounded-md mt-4 flex justify-between"
        >
            <div
                className="flex items-center cursor-pointer"
                onClick={() => backToProductPage(item)}
            >
                <Iconify
                    icon={IconCode.addedToCart}
                    color="black"
                    width="36px"
                    className="mr-2"
                />
                <div>
                    <p className="text-black text-base font-bold">
                        {item?.itemName}
                    </p>
                    <div className="flex items-center">
                        <Iconify
                            icon={IconCode.rupeeBold}
                            color="black"
                            width="16px"
                        />
                        <p className="text-black text-base font-bold">
                            {item.rate}
                        </p>
                    </div>
                </div>
            </div>
            <CustomIconButton
                onClick={() => removeProductFromCart(item?.itemId)}
                className="relative z-50"
            >
                <Iconify
                    icon={IconCode.delete}
                    width="18px"
                    className="text-primary"
                />
            </CustomIconButton>
        </div>
    ));

    return (
        <>
            {/* ✅ Custom Drawer */}
            <div
                onClick={handleCloseCart}
                className={`fixed inset-0 z-50 transition-all duration-300 ${
                    openCart
                        ? "visible bg-black/40"
                        : "invisible bg-transparent"
                }`}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    className={`absolute right-0 top-0 h-full w-96 bg-slate-100 shadow-lg transition-transform duration-300 flex flex-col ${
                        openCart ? "translate-x-0" : "translate-x-full"
                    }`}
                >
                    {isItemInCart?.centreTest?.length > 0 ? (
                        <>
                            {/* Header */}
                            <div className="flex justify-between items-center p-4 border-b">
                                <div className="flex items-center">
                                    <CustomIconButton
                                        onClick={handleCloseCart}
                                        className="w-8 h-8 flex items-center justify-center p-0 mr-1"
                                    >
                                        <Iconify
                                            icon={IconCode.arrowDown}
                                            width="32px"
                                            className="text-primary rotate-90"
                                        />
                                    </CustomIconButton>
                                    <h4 className="text-black font-bold text-base">
                                        Cart
                                    </h4>
                                </div>
                                <h4
                                    className="text-primary font-bold uppercase text-sm cursor-pointer"
                                    onClick={clearAllCartHandler}
                                >
                                    Clear All
                                </h4>
                            </div>

                            {/* Body */}
                            <div className="flex-1 overflow-y-auto p-4">
                                <h4 className="text-black font-bold text-lg mb-2">
                                    {isItemInCart?.centreName}
                                </h4>
                                {DrawerList}
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t">
                                <div className="flex items-center mb-3">
                                    <p className="text-black text-base font-bold">
                                        Total Amount :
                                    </p>
                                    <Iconify
                                        icon={IconCode.rupeeBold}
                                        color="black"
                                        width="16px"
                                        className="mx-1"
                                    />
                                    <p className="text-black text-base font-bold">
                                        {totalAmount}
                                    </p>
                                </div>
                                <CustomButton
                                    fullWidth
                                    variant="contained"
                                    className="w-full"
                                    onClick={handleClickOpen}
                                >
                                    Place Order
                                </CustomButton>
                            </div>
                        </>
                    ) : (
                        <NoTestFoundInCart findTest={findTest} />
                    )}
                </div>
            </div>

            {/* Login popup */}
            <LoginPopUp
                setOpen={setOpen}
                open={open}
                onClose={onClose}
                title="Sign Up / Login"
            />
        </>
    );
};

export default CartDrawer;
